import { useTranslate } from '../../application/i18n/LocaleProvider';
import { GRADIENT_KEYS, SURFACE_KEYS, TRACTION_CONTROL } from '../../domain/race/raceConditions';
import { ChevronIcon } from '../icons/ControlIcons';

/**
 * The road, chosen (SPEC-008 PR-8.5, PR-8.7).
 *
 * Three groups of radio buttons, BEHIND A DISCLOSURE. They are `radio` inputs
 * rather than the `aria-pressed` toggles the garage's facets use, and the
 * difference is real rather than cosmetic: a facet is a set the reader adds to
 * and removes from, where a surface is one of three and picking snow un-picks
 * dry. A row of toggles that silently deselect each other is a radio group that
 * has to explain itself to a screen reader; a radio group already is one.
 *
 * Collapsed by default because of what they cost when they are not. Three
 * labelled groups of chips is most of a phone screen, and open by default they
 * pushed the two cars and the strip below the fold — so this screen opened on a
 * control panel rather than on the two cars it exists to race. The default road
 * is also the RIGHT one for most visits: dry, flat and aided is the condition
 * every figure in the catalogue was measured under.
 *
 * `<details>` rather than a button and a piece of state, for the reason the
 * picker uses a native `<select>`: the platform's disclosure is keyboard- and
 * screen-reader-complete without a line of code here, it opens without
 * JavaScript, and there is no second copy of "is it open" to get out of step
 * with the markup.
 *
 * THE SUMMARY ALWAYS STATES THE ROAD, open or closed, and that is the whole
 * reason collapsing these is safe. Hiding a control is fine; hiding the fact
 * that this race is being run on snow is not, and a reader who opened a shared
 * `?surface=snow` link would otherwise have no way to know why the times look
 * wrong. So the disclosure hides the CHOOSING and never the CHOICE.
 *
 * Every label is a composed key — `compare.surface.snow` — which is exactly why
 * those vocabularies are closed. A value from outside one would render its own
 * key at the reader.
 */

const GROUPS = [
    { key: 'surface', values: SURFACE_KEYS },
    { key: 'gradient', values: GRADIENT_KEYS },
    { key: 'tractionControl', values: TRACTION_CONTROL },
];

function ConditionGroup({ conditions, group, onChange }) {
    const t = useTranslate();
    const { key, values } = group;

    return (
        <fieldset className="race-conditions__group">
            <legend className="race-conditions__legend">{t(`compare.${key}`)}</legend>

            <div className="race-conditions__options">
                {values.map((value) => (
                    <label className="race-chip" key={value}>
                        {/* The input is the state and the tint is the third
                            carrier of it, per the garage's own rule: a row told
                            apart only by an orange wash is a row somebody with a
                            colour deficiency has to guess at. */}
                        <input
                            type="radio"
                            className="race-chip__input"
                            name={`race-${key}`}
                            value={value}
                            checked={conditions[key] === value}
                            onChange={() => onChange(key, value)}
                        />
                        <span className="race-chip__label">{t(`compare.${key}.${value}`)}</span>
                    </label>
                ))}
            </div>
        </fieldset>
    );
}

export default function DragRaceConditions({ conditions, onChange }) {
    const t = useTranslate();
    // All three, always — including the two that are at their default. A summary
    // that named only what had been changed would say nothing at all on the
    // common visit, which is the one where a reader most needs telling what road
    // they are about to watch a race on.
    const stated = [
        t(`compare.surface.${conditions.surface}`),
        t(`compare.gradient.${conditions.gradient}`),
        t(`compare.conditions.aid.${conditions.tractionControl}`),
    ].join(t('compare.conditions.separator'));

    return (
        <details className="race-conditions">
            <summary className="race-conditions__summary">
                <span className="race-conditions__stated">{stated}</span>
                <span className="race-conditions__toggle">
                    {t('compare.conditions.change')}
                    <ChevronIcon className="race-conditions__chevron" size={16} />
                </span>
            </summary>

            <div className="race-conditions__groups">
                {GROUPS.map((group) => (
                    <ConditionGroup key={group.key} group={group} conditions={conditions} onChange={onChange} />
                ))}
            </div>
        </details>
    );
}
